import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Popconfirm,
  Space,
  Spin,
  Tabs,
  Tag,
  Timeline,
  Typography,
  message,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  ApiService,
  ProcessHistoryEntry,
  ProcessVersionRow,
  TemplateProcess,
} from "../../api";

const { Text, Title } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
  templateId: number;
  /** The revision the page was last read at, for the restore conflict check. */
  revision?: string;
  onRestored: (process: TemplateProcess) => void;
};

const when = (value: string | null) =>
  value ? new Date(value).toLocaleString() : "";

/**
 * Who changed this process, and what each published version contained.
 *
 * Two questions an auditor asks about a permit system, and until now neither
 * had an answer in the product: "who changed the approval rules" and "what did
 * they look like in March". The restore action closes the loop — a bad publish
 * can be undone instead of reconstructed by hand.
 */
const ProcessHistoryDrawer = ({
  open,
  onClose,
  templateId,
  revision,
  onRestored,
}: Props) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<ProcessHistoryEntry[]>([]);
  const [versions, setVersions] = useState<ProcessVersionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [restoring, setRestoring] = useState<number | null>(null);
  const [skipped, setSkipped] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [history, versionList] = await Promise.all([
        ApiService.apiMainTemplateProcessHistoryRetrieve(templateId),
        ApiService.apiMainTemplateProcessVersionsRetrieve(templateId),
      ]);
      setEntries(history.entries);
      setVersions(versionList.versions);
    } catch (e: any) {
      setError(e?.body?.message ?? t("Something went wrong"));
    }
    setLoading(false);
  }, [templateId, t]);

  // Only fetch when it is actually opened, and refetch each time — a drawer
  // showing a stale trail is worse than one that takes a moment.
  useEffect(() => {
    if (open) {
      setSkipped([]);
      load();
    }
  }, [open, load]);

  const restore = async (version: number) => {
    setRestoring(version);
    try {
      const result = await ApiService.apiMainTemplateProcessRestoreCreate(
        templateId,
        version,
        { publish: true, base_revision: revision }
      );
      onRestored(result);
      // Parts of a restore can be impossible — a section deleted since. Saying
      // so is the difference between a trustworthy feature and a puzzling one.
      if (result.skipped?.length) {
        setSkipped(result.skipped);
        await load();
      } else {
        message.success(t("Restored version {{n}}").replace("{{n}}", String(version)));
        onClose();
      }
    } catch (e: any) {
      message.error(e?.body?.message ?? t("Something went wrong"));
    }
    setRestoring(null);
  };

  const changeList = (changes: string[]) =>
    changes.length ? (
      <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
        {changes.map((c, i) => (
          <li key={i}>
            <Text type="secondary">{c}</Text>
          </li>
        ))}
      </ul>
    ) : (
      <Text type="secondary">{t("No details recorded.")}</Text>
    );

  return (
    <Drawer
      title={t("Process history")}
      open={open}
      onClose={onClose}
      width={620}
    >
      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      )}
      {skipped.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t("Part of that version could not be restored")}
          description={
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {skipped.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          }
        />
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <Tabs
          items={[
            {
              key: "changes",
              label: t("Changes"),
              children: entries.length === 0 ? (
                <Empty description={t("No changes recorded yet.")} />
              ) : (
                <Timeline
                  items={entries.map((entry) => ({
                    children: (
                      <div>
                        <Space wrap size={6}>
                          <Text strong>{t(entry.action)}</Text>
                          {entry.version != null && (
                            <Tag>
                              {t("Version")} {entry.version}
                            </Tag>
                          )}
                        </Space>
                        <div>
                          <Text type="secondary">
                            {entry.actor_name} · {when(entry.created_at)}
                          </Text>
                        </div>
                        {changeList(entry.changes)}
                      </div>
                    ),
                  }))}
                />
              ),
            },
            {
              key: "versions",
              label: t("Versions"),
              children: versions.length === 0 ? (
                <Empty description={t("Nothing has been published yet.")} />
              ) : (
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                  {versions.map((version) => (
                    <div key={version.version}>
                      <Space
                        wrap
                        style={{ width: "100%", justifyContent: "space-between" }}
                      >
                        <Space wrap size={8}>
                          <Title level={5} style={{ margin: 0 }}>
                            {t("Version")} {version.version}
                          </Title>
                          {version.is_current && (
                            <Tag color="success">{t("Live now")}</Tag>
                          )}
                          {version.restored_from != null && (
                            <Tag>
                              {t("Restored from")} {version.restored_from}
                            </Tag>
                          )}
                        </Space>
                        {!version.is_current && (
                          <Popconfirm
                            title={t("Restore this version?")}
                            description={t(
                              "The process goes back to how it was in this version and is published. Permits already in progress are not affected."
                            )}
                            okText={t("Restore")}
                            cancelText={t("Cancel")}
                            onConfirm={() => restore(version.version)}
                          >
                            <Button
                              size="small"
                              loading={restoring === version.version}
                            >
                              {t("Restore")}
                            </Button>
                          </Popconfirm>
                        )}
                      </Space>
                      <div>
                        <Text type="secondary">
                          {version.published_by_name || t("system")} ·{" "}
                          {when(version.published_at)}
                        </Text>
                      </div>
                      <Space wrap size={4} style={{ margin: "6px 0" }}>
                        {version.stage_names.map((name, i) => (
                          <Tag key={i}>{t(name)}</Tag>
                        ))}
                      </Space>
                      {changeList(version.changes)}
                    </div>
                  ))}
                </Space>
              ),
            },
          ]}
        />
      )}
    </Drawer>
  );
};

export default ProcessHistoryDrawer;
